import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import ChessRestClient from '@/Api/ChessRestClient';

type Scope = 'trouble' | 'prominent' | 'all';
type StageStatus = 'stuck' | 'all';

type Stage = {
  key: string;
  order: number;
  label: string;
  source: string;
  broken_when: string;
  manual_checks: string[];
  counts: {
    healthy: number;
    warning: number;
    broken: number;
    users: number;
  };
};

type HealthUser = {
  user: {
    name: string;
    mobile_number: string;
  };
  order: {
    order_id: string;
    status: string;
    duration_in_months: number;
  };
  health: {
    severity: 'broken' | 'warning' | 'healthy';
    primary_stage: string;
    broken_stages: string[];
    warning_stages: string[];
    stuck_reasons: {
      stage: string;
      severity: 'broken' | 'warning' | 'healthy';
      message: string;
      evidence: Record<string, any>;
      resolution: string;
    }[];
  };
  support: {
    latest_issue_summary: string;
    latest_request_type: string;
    open_issue_count: number;
    pending_request_count: number;
    failed_outbox_count: number;
  };
  suggested_actions: string[];
};

type DashboardPayload = {
  cached: boolean;
  summary: Record<string, number>;
  stages: Stage[];
  users: HealthUser[];
};

const scopeLabels: Record<Scope, string> = {
  trouble: 'Trouble only',
  prominent: 'Prominent stuck',
  all: 'All users',
};

const CSOCHealthDashboard = ({dashboardData}) => {
  const userData = dashboardData?.user || {};
  const isStaff = Boolean(userData?.is_staff);
  const [scope, setScope] = useState<Scope>('trouble');
  const [stage, setStage] = useState('');
  const [stageStatus, setStageStatus] = useState<StageStatus>('stuck');
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState<DashboardPayload | null>(null);

  const summary = payload?.summary || {};

  const loadDashboard = useCallback(
    async (refresh = false) => {
      if (!isStaff) {
        return;
      }
      try {
        setLoading(true);
        setError('');
        const params = new URLSearchParams({
          scope,
          stage_status: stageStatus,
          days,
          limit: '200',
        });
        if (stage) {
          params.set('stage', stage);
        }
        if (search.trim()) {
          params.set('search', search.trim());
        }
        if (issueFilter) {
          params.set('issue', issueFilter);
        }
        if (refresh) {
          params.set('refresh', '1');
        }

        const response = await ChessRestClient.get(
          `subscription/v1/csoc/health-dashboard?${params.toString()}`,
        );
        setPayload(response.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Unable to load CSOC health dashboard',
        );
      } finally {
        setLoading(false);
      }
    },
    [days, isStaff, issueFilter, scope, search, stage, stageStatus],
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectedStageLabel = useMemo(() => {
    if (!stage) {
      return 'all stages';
    }
    return payload?.stages?.find(item => item.key === stage)?.label || stage;
  }, [payload?.stages, stage]);

  if (!isStaff) {
    return (
      <View style={styles.denied}>
        <Text style={styles.deniedTitle}>Staff access required</Text>
        <Text style={styles.muted}>
          This CSOC operations dashboard is only available to staff users.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.content}>
      <View style={styles.topbar}>
        <View>
          <Text style={styles.title}>CSOC Health Dashboard</Text>
          <Text style={styles.muted}>
            Live API view. Default load shows users in trouble.
          </Text>
        </View>
        <View style={styles.scopeGroup}>
          {(Object.keys(scopeLabels) as Scope[]).map(item => (
            <TouchableOpacity
              key={item}
              style={[styles.scopeButton, scope === item && styles.scopeActive]}
              onPress={() => {
                setScope(item);
                setStageStatus(item === 'all' ? 'all' : 'stuck');
              }}>
              <Text
                style={[
                  styles.scopeText,
                  scope === item && styles.scopeTextActive,
                ]}>
                {scopeLabels[item]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.toolbar}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          onSubmitEditing={() => loadDashboard()}
          placeholder="Search mobile, order id, user, batch, issue id"
          style={[styles.field, styles.search]}
        />
        {['7', '30', '90', '365'].map(item => (
          <TouchableOpacity
            key={item}
            style={[styles.field, days === item && styles.filterActive]}
            onPress={() => setDays(item)}>
            <Text style={styles.filterText}>{item}d</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity
          style={[styles.field, issueFilter && styles.filterActive]}
          onPress={() =>
            setIssueFilter(value => (value ? '' : 'placeholder_long_order'))
          }>
          <Text style={styles.filterText}>Placeholder / long order</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.field}
          onPress={() => loadDashboard(true)}>
          <Text style={styles.filterText}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.metrics}>
        <Metric
          title="Trouble users"
          value={summary.trouble_users}
          tone="red"
        />
        <Metric
          title="Prominent stuck"
          value={summary.prominent_stuck_users}
          tone="red"
        />
        <Metric
          title="Placeholder batch"
          value={summary.placeholder_batch_users}
          tone="red"
        />
        <Metric
          title="Long-order batch"
          value={summary.long_order_batch_users}
          tone="amber"
        />
        <Metric
          title="Open issues"
          value={summary.open_user_issues}
          tone="amber"
        />
        <Metric
          title="Pending requests"
          value={summary.pending_support_requests}
          tone="amber"
        />
        <Metric
          title="Outbox failures"
          value={summary.failed_external_actions}
          tone="red"
        />
        <Metric
          title="Healthy users"
          value={summary.healthy_users}
          tone="green"
        />
      </View>

      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View>
            <Text style={styles.panelTitle}>Lifecycle Flow</Text>
            <Text style={styles.muted}>
              Click a stage to show users stuck there. Current stage:{' '}
              {selectedStageLabel}
            </Text>
          </View>
          <Text style={styles.cacheBadge}>
            {loading ? 'loading' : payload?.cached ? 'cached' : 'fresh'}
          </Text>
        </View>
        <ScrollView horizontal contentContainerStyle={styles.flow}>
          {payload?.stages?.map(stageItem => (
            <StageCard
              key={stageItem.key}
              stage={stageItem}
              selected={stage === stageItem.key}
              onPress={() => {
                setStage(stageItem.key);
                setStageStatus('stuck');
                setScope('trouble');
              }}
            />
          ))}
        </ScrollView>
      </View>

      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View>
            <Text style={styles.panelTitle}>
              {payload?.users?.length || 0} users
            </Text>
            <Text style={styles.muted}>
              Sorted by severity, stage, and support signal.
            </Text>
          </View>
        </View>
        <View style={styles.table}>
          {payload?.users?.map(row => (
            <UserRow
              key={`${row.order.order_id}-${row.user.mobile_number}`}
              row={row}
            />
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const Metric = ({title, value = 0, tone}) => (
  <View style={[styles.metric, styles[`${tone}Border`]]}>
    <Text style={styles.metricTitle}>{title}</Text>
    <Text style={[styles.metricValue, styles[`${tone}Text`]]}>
      {value || 0}
    </Text>
  </View>
);

const StageCard = ({stage, selected, onPress}) => {
  const counts = stage.counts || {};
  const tone =
    counts.broken > 0 ? 'red' : counts.warning > 0 ? 'amber' : 'green';
  return (
    <TouchableOpacity
      style={[styles.stageCard, selected && styles.stageSelected]}
      onPress={onPress}>
      <View style={[styles.stageNumber, styles[`${tone}Bg`]]}>
        <Text style={styles.stageNumberText}>{stage.order}</Text>
      </View>
      <Text style={styles.stageTitle}>{stage.label}</Text>
      <Text style={styles.stageSource}>{stage.source}</Text>
      <View style={styles.badgeRow}>
        <Badge label={`${counts.healthy || 0} ok`} tone="green" />
        <Badge label={`${counts.warning || 0} warn`} tone="amber" />
        <Badge label={`${counts.broken || 0} bad`} tone="red" />
      </View>
      <Text style={styles.stageDetails} numberOfLines={4}>
        Resolve: {(stage.manual_checks || [])[0]}
      </Text>
    </TouchableOpacity>
  );
};

const Badge = ({label, tone}) => (
  <Text style={[styles.badge, styles[`${tone}Soft`], styles[`${tone}Text`]]}>
    {label}
  </Text>
);

const UserRow = ({row}: {row: HealthUser}) => {
  const severity = row.health.severity;
  const stuckReasons = row.health.stuck_reasons || [];
  const actions = row.suggested_actions?.length
    ? row.suggested_actions.slice(0, 3)
    : ['No intervention required. Re-check if a new issue appears.'];
  return (
    <View style={styles.userRow}>
      <View style={styles.userCell}>
        <Text style={styles.userName}>{row.user.name || 'Unknown'}</Text>
        <Text style={styles.mini}>{row.user.mobile_number}</Text>
      </View>
      <View style={styles.orderCell}>
        <Text style={styles.orderText}>{row.order.order_id}</Text>
        <Text style={styles.mini}>
          {row.order.status}, {row.order.duration_in_months || 0} months
        </Text>
      </View>
      <View style={styles.stageCell}>
        <Badge
          label={row.health.primary_stage}
          tone={
            severity === 'broken'
              ? 'red'
              : severity === 'warning'
              ? 'amber'
              : 'green'
          }
        />
        <Text style={styles.mini}>{row.health.broken_stages?.join(', ')}</Text>
      </View>
      <View style={styles.issueCell}>
        <Text style={styles.issueText}>
          {row.support.latest_issue_summary ||
            row.support.latest_request_type ||
            'No support signal'}
        </Text>
        <Text style={styles.mini}>
          Issues: {row.support.open_issue_count || 0}, requests:{' '}
          {row.support.pending_request_count || 0}, outbox failed:{' '}
          {row.support.failed_outbox_count || 0}
        </Text>
      </View>
      <View style={styles.whyCell}>
        {stuckReasons.length ? (
          stuckReasons.slice(0, 4).map(reason => (
            <View
              key={`${reason.stage}-${reason.message}`}
              style={styles.whyBox}>
              <Text style={styles.whyTitle}>
                {reason.stage}: {reason.message}
              </Text>
              <Text style={styles.mini}>
                Evidence: {JSON.stringify(reason.evidence || {})}
              </Text>
            </View>
          ))
        ) : (
          <Text style={styles.mini}>No stuck reason for current filters.</Text>
        )}
      </View>
      <View style={styles.resolutionCell}>
        {(stuckReasons.length
          ? stuckReasons.slice(0, 3).map(reason => reason.resolution)
          : actions
        ).map(action => (
          <Text key={action} style={styles.resolutionText}>
            {action}
          </Text>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {flex: 1, backgroundColor: '#eef3f8'},
  content: {padding: 24, gap: 16},
  topbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  title: {fontSize: 31, fontWeight: '900', color: '#111827'},
  muted: {color: '#5c6678'},
  scopeGroup: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  scopeButton: {paddingVertical: 12, paddingHorizontal: 18},
  scopeActive: {backgroundColor: '#111827'},
  scopeText: {fontWeight: '800', color: '#5c6678'},
  scopeTextActive: {color: '#fff'},
  toolbar: {flexDirection: 'row', flexWrap: 'wrap', gap: 10},
  field: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    paddingHorizontal: 13,
    justifyContent: 'center',
    backgroundColor: '#fff',
  },
  search: {minWidth: 340, flex: 1},
  filterActive: {backgroundColor: '#e8f6f4', borderColor: '#9fcfca'},
  filterText: {fontWeight: '800', color: '#111827'},
  error: {
    color: '#ba2424',
    backgroundColor: '#fff3f3',
    borderColor: '#efb3b3',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
  },
  metrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metric: {
    width: 210,
    minHeight: 104,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderLeftWidth: 5,
    backgroundColor: '#fff',
    padding: 15,
  },
  metricTitle: {fontSize: 13, color: '#5c6678'},
  metricValue: {fontSize: 34, lineHeight: 40, fontWeight: '900'},
  panel: {
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  panelHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  panelTitle: {fontSize: 18, fontWeight: '900', color: '#111827'},
  cacheBadge: {
    backgroundColor: '#edf4ff',
    color: '#255fb3',
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 10,
    fontWeight: '900',
    alignSelf: 'flex-start',
  },
  flow: {padding: 22, gap: 22},
  stageCard: {
    width: 190,
    minHeight: 190,
    borderWidth: 1,
    borderColor: '#cfd8e3',
    borderRadius: 8,
    backgroundColor: '#fbfcfe',
    padding: 13,
    paddingTop: 30,
  },
  stageSelected: {borderColor: '#255fb3', borderWidth: 2},
  stageNumber: {
    position: 'absolute',
    top: -18,
    left: 14,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stageNumberText: {color: '#fff', fontWeight: '900'},
  stageTitle: {fontSize: 16, fontWeight: '900', color: '#111827'},
  stageSource: {fontSize: 12, color: '#5c6678', marginTop: 6},
  badgeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12},
  badge: {
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 12,
    fontWeight: '900',
    overflow: 'hidden',
  },
  stageDetails: {fontSize: 12, color: '#5c6678', marginTop: 12, lineHeight: 17},
  table: {minWidth: 1400},
  userRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#cfd8e3',
    padding: 14,
    gap: 14,
  },
  userCell: {width: 170},
  orderCell: {width: 150},
  stageCell: {width: 170},
  issueCell: {flex: 1, minWidth: 280},
  whyCell: {flex: 1.2, minWidth: 340},
  resolutionCell: {flex: 1, minWidth: 280},
  userName: {fontSize: 16, fontWeight: '900', color: '#111827'},
  orderText: {fontWeight: '800', color: '#111827'},
  issueText: {color: '#111827', lineHeight: 20},
  mini: {fontSize: 13, color: '#5c6678', marginTop: 3},
  whyBox: {
    borderLeftWidth: 4,
    borderLeftColor: '#ba2424',
    backgroundColor: '#fff3f3',
    padding: 8,
    marginBottom: 6,
  },
  whyTitle: {fontWeight: '900', color: '#111827', lineHeight: 19},
  resolutionText: {
    borderLeftWidth: 4,
    borderLeftColor: '#9e5f00',
    backgroundColor: '#fff6db',
    padding: 8,
    marginBottom: 6,
    lineHeight: 19,
  },
  denied: {padding: 28},
  deniedTitle: {fontSize: 24, fontWeight: '900', color: '#111827'},
  redBorder: {borderLeftColor: '#ba2424'},
  amberBorder: {borderLeftColor: '#9e5f00'},
  greenBorder: {borderLeftColor: '#11724e'},
  redText: {color: '#ba2424'},
  amberText: {color: '#9e5f00'},
  greenText: {color: '#11724e'},
  redBg: {backgroundColor: '#ba2424'},
  amberBg: {backgroundColor: '#9e5f00'},
  greenBg: {backgroundColor: '#11724e'},
  redSoft: {backgroundColor: '#fff3f3'},
  amberSoft: {backgroundColor: '#fff6db'},
  greenSoft: {backgroundColor: '#e9f8f1'},
});

export default CSOCHealthDashboard;
